import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconBook,
  IconCalendar,
  IconArrowUpRight,
  IconArrowDownRight,
  IconScale,
  IconWallet,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useLedgerReport } from "../controllers/reports.controller";
import { useTheme } from "../contexts/ThemeContext";
import ReportExportWrapper from "../components/ReportExportWrapper";

export default function LedgerReportPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().substring(0, 10));
  const [accountFilter, setAccountFilter] = useState("all");

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error } = useLedgerReport({ from: dateFrom, to: dateTo, account: accountFilter === "all" ? null : accountFilter });

  if (isLoading) return <Page><div className="flex items-center justify-center h-full"><span className="loading loading-spinner loading-lg text-restro-green"></span></div></Page>;
  if (error) return <Page>Error loading ledger.</Page>;

  const isPayable = accountFilter.toLowerCase().includes("payable");
  const isReceivable = accountFilter.toLowerCase().includes("receivable");
  const isLiabilityOrRevenue = isPayable || accountFilter.toLowerCase().includes("tax") || accountFilter.toLowerCase().includes("sales") || accountFilter.toLowerCase().includes("revenue");

  let ledger = [...(data?.ledger || [])];
  let summary = { ...(data?.summary || {}) };
  const openingBalance = data?.opening_balance || 0;

  if (searchQuery) {
    ledger = ledger.filter(entry => entry.description?.toLowerCase().includes(searchQuery.toLowerCase()) || entry.account?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Recalculate running balance and summary for search-filtered rows
    let searchRunningBalance = 0;
    let total_debit = 0;
    let total_credit = 0;

    ledger = ledger.map(entry => {
      const deb = parseFloat(String(entry.debit || 0).replace(/,/g, "")) || 0;
      const cred = parseFloat(String(entry.credit || 0).replace(/,/g, "")) || 0;
      total_debit += deb;
      total_credit += cred;

      if (isPayable || isLiabilityOrRevenue) {
        searchRunningBalance += (cred - deb);
      } else {
        searchRunningBalance += (deb - cred);
      }

      return {
        ...entry,
        debit: deb,
        credit: cred,
        balance: searchRunningBalance
      };
    });

    const closing_balance = (isPayable || isLiabilityOrRevenue)
      ? (total_credit - total_debit)
      : (total_debit - total_credit);

    summary = {
      opening_balance: 0,
      total_debit,
      total_credit,
      closing_balance,
      balance: closing_balance,
      is_payable: isPayable,
      is_receivable: isReceivable
    };
  }

  const accounts = ["all", "Sales", "Room Revenue", "Restaurant Revenue", "Inventory", "Accounts Receivable", "Accounts Payable", "Tax Payable", "Cash/Bank", "Expenses", "General Expense", "Unearned Room Revenue"];

  const balanceLabel = isPayable ? "Payable Balance" : isReceivable ? "Receivable Balance" : "Balance";

  const columns = [
    { header: "Date", key: "date" },
    { header: "Account", key: "account" },
    { header: "Description / Particulars", key: "description" },
    { header: "Debit (+)", key: "debit" },
    { header: "Credit (-)", key: "credit" },
    { header: balanceLabel, key: "balance" }
  ];

  const tableData = [];
  if (!searchQuery && openingBalance !== 0) {
    tableData.push({
      date: new Date(dateFrom).toLocaleDateString(),
      account: accountFilter === "all" ? "All Accounts" : accountFilter,
      description: "Opening Balance (B/F)",
      debit: "-",
      credit: "-",
      balance: Number(openingBalance).toFixed(2)
    });
  }

  ledger.forEach(entry => {
    tableData.push({
      date: new Date(entry.entry_date).toLocaleDateString(),
      account: entry.account,
      description: entry.description,
      debit: (Number(entry.debit) || 0) > 0 ? Number(entry.debit).toFixed(2) : "-",
      credit: (Number(entry.credit) || 0) > 0 ? Number(entry.credit).toFixed(2) : "-",
      balance: Number(entry.balance || 0).toFixed(2)
    });
  });

  const summaryData = {
    ...((!searchQuery && openingBalance !== 0) ? { "Opening Balance": Number(openingBalance).toFixed(2) } : {}),
    "Total Debits": Number(summary.total_debit || 0).toFixed(2),
    "Total Credits": Number(summary.total_credit || 0).toFixed(2),
    [balanceLabel]: Number(summary.closing_balance ?? summary.balance ?? 0).toFixed(2)
  };

  const activeClosingBalance = Number(summary.closing_balance ?? summary.balance ?? 0);

  const totalPages = Math.max(1, Math.ceil(ledger.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedLedger = ledger.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  return (
    <Page>
      <ReportExportWrapper
        title="General Ledger Report"
        reportType="Accounting"
        dateRange={`${dateFrom} to ${dateTo}`}
        filters={`Account: ${accountFilter === 'all' ? 'All Accounts' : accountFilter} | Search: ${searchQuery || 'None'}`}
        columns={columns}
        tableData={tableData}
        summaryData={summaryData}
      >
        <div className="flex flex-col gap-4 mb-8 print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="text-2xl font-bold flex items-center gap-2"><IconBook /> General Ledger Report</h3>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-2 items-center bg-white dark:bg-restro-gray p-1 rounded-xl border border-gray-200 dark:border-gray-800 text-xs">
                <IconCalendar size={16} className="text-gray-400 ml-2" />
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-transparent outline-none w-28 font-bold" />
                <span className="text-gray-400">to</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-transparent outline-none w-28 font-bold" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} className="select select-sm select-bordered rounded-xl w-52 font-medium">
              {accounts.map(acc => <option key={acc} value={acc}>{acc === 'all' ? 'All Accounts' : acc}</option>)}
            </select>
            <input 
              type="text" 
              placeholder="Search by supplier, customer, description..." 
              className="input input-sm input-bordered rounded-xl w-full max-w-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* SUMMARY STAT CARDS */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${(!searchQuery && openingBalance !== 0) ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 mb-8 print:hidden`}>
          {!searchQuery && openingBalance !== 0 && (
            <div className="p-4 bg-white dark:bg-restro-gray border border-gray-200 dark:border-gray-800 rounded-2xl flex justify-between items-center shadow-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Opening Balance</p>
                <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-200">{Number(openingBalance).toFixed(2)}</h3>
                <span className="text-[10px] text-gray-400">Balance prior to {dateFrom}</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300">
                <IconScale size={20} />
              </div>
            </div>
          )}

          <div className="p-4 bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl flex justify-between items-center shadow-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">
                {isReceivable ? "Total Invoiced (+Dr)" : isPayable ? "Total Paid / Settled (-Dr)" : "Total Debits"}
              </p>
              <h3 className="text-2xl font-bold text-blue-600">{(Number(summary.total_debit) || 0).toFixed(2)}</h3>
              <span className="text-[10px] text-gray-400">Debit Entries (Dr.)</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600">
              <IconArrowUpRight />
            </div>
          </div>

          <div className="p-4 bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl flex justify-between items-center shadow-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">
                {isReceivable ? "Total Collected / Ret (-Cr)" : isPayable ? "Total Invoiced / Due (+Cr)" : "Total Credits"}
              </p>
              <h3 className="text-2xl font-bold text-green-600">{(Number(summary.total_credit) || 0).toFixed(2)}</h3>
              <span className="text-[10px] text-gray-400">Credit Entries (Cr.)</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-950 flex items-center justify-center text-green-600">
              <IconArrowDownRight />
            </div>
          </div>

          <div className={`p-4 rounded-2xl flex justify-between items-center shadow-sm border ${
            isPayable 
              ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800' 
              : isReceivable 
                ? 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800'
                : 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800'
          }`}>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs uppercase font-extrabold tracking-wider text-gray-700 dark:text-gray-200">
                  {isPayable ? "Active Total Payable" : isReceivable ? "Active Total Receivable" : "Closing Balance"}
                </span>
                <span className={`badge badge-xs font-semibold ${
                  isPayable ? 'badge-warning' : isReceivable ? 'badge-info' : 'badge-success'
                }`}>
                  Active
                </span>
              </div>
              <h3 className={`text-2xl font-black ${
                isPayable ? 'text-amber-800 dark:text-amber-300' : isReceivable ? 'text-blue-800 dark:text-blue-300' : 'text-emerald-800 dark:text-emerald-300'
              }`}>
                {activeClosingBalance.toFixed(2)}
              </h3>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                {isPayable ? "Net Due to Suppliers" : isReceivable ? "Net Due from Customers" : "Net Balance"}
              </span>
            </div>
            <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
              isPayable ? 'bg-amber-100 dark:bg-amber-900 text-amber-700' : isReceivable ? 'bg-blue-100 dark:bg-blue-900 text-blue-700' : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700'
            }`}>
              <IconWallet size={22} />
            </div>
          </div>
        </div>

        {/* LEDGER TABLE */}
        <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl overflow-hidden print:border-none print:shadow-none shadow-sm">
          <div className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead>
                <tr className="bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wider">
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Account</th>
                  <th className="py-3 px-4">Particulars / Description</th>
                  <th className="py-3 px-4 text-right">Debit (Dr.)</th>
                  <th className="py-3 px-4 text-right">Credit (Cr.)</th>
                  <th className="py-3 px-4 text-right font-bold text-gray-900 dark:text-white bg-gray-200/50 dark:bg-gray-700/50">
                    {balanceLabel}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {/* Opening Balance Row only on page 1 */}
                {!searchQuery && openingBalance !== 0 && safeCurrentPage === 1 && (
                  <tr className="bg-amber-50/50 dark:bg-amber-950/20 font-semibold border-b border-amber-200/50">
                    <td className="py-3 px-4 text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">{new Date(dateFrom).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap text-gray-700 dark:text-gray-200">{accountFilter === 'all' ? 'All Accounts' : accountFilter}</td>
                    <td className="py-3 px-4 font-semibold text-gray-800 dark:text-gray-100">
                      Opening Balance <span className="text-xs font-normal text-gray-500 italic">(Brought Forward)</span>
                    </td>
                    <td className="py-3 px-4 text-right text-gray-400 font-mono">-</td>
                    <td className="py-3 px-4 text-right text-gray-400 font-mono">-</td>
                    <td className="py-3 px-4 text-right font-black font-mono bg-amber-100/30 dark:bg-amber-900/20 text-gray-900 dark:text-gray-100">
                      {Number(openingBalance).toFixed(2)}
                    </td>
                  </tr>
                )}

                {paginatedLedger.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50/80 dark:hover:bg-black/20 transition">
                    <td className="py-3 px-4 text-xs whitespace-nowrap text-gray-500">{new Date(entry.entry_date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 font-medium text-xs whitespace-nowrap">{entry.account}</td>
                    <td className="py-3 px-4 max-w-sm truncate text-gray-700 dark:text-gray-300" title={entry.description}>
                      {entry.description || "-"}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-medium text-blue-600">
                      {(Number(entry.debit) || 0) > 0 ? Number(entry.debit).toFixed(2) : "-"}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-medium text-green-600">
                      {(Number(entry.credit) || 0) > 0 ? Number(entry.credit).toFixed(2) : "-"}
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-bold bg-gray-50/60 dark:bg-gray-800/30 ${
                      isPayable
                        ? Number(entry.balance) > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'
                        : isReceivable
                          ? Number(entry.balance) > 0 ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-800 dark:text-gray-100'
                    }`}>
                      {Number(entry.balance || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              
              {/* Totals row for screen and print view */}
              <tfoot className="font-bold border-t-2 border-gray-300 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-800/90">
                <tr>
                  <td colSpan="3" className="py-3 px-4 text-right font-extrabold text-gray-700 dark:text-gray-200">
                    Totals & Closing {balanceLabel}:
                  </td>
                  <td className="py-3 px-4 text-right text-blue-600 font-mono text-base font-black">
                    {(Number(summary.total_debit) || 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-right text-green-600 font-mono text-base font-black">
                    {(Number(summary.total_credit) || 0).toFixed(2)}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono text-base font-black bg-gray-200/60 dark:bg-gray-700/60 ${
                    isPayable ? 'text-amber-800 dark:text-amber-300' : isReceivable ? 'text-blue-800 dark:text-blue-300' : 'text-emerald-800 dark:text-emerald-300'
                  }`}>
                    {activeClosingBalance.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {ledger.length === 0 && (!openingBalance || openingBalance === 0) && (
            <div className="p-20 text-center text-gray-500 italic">No entries found for this period.</div>
          )}

          {/* Pagination bar */}
          {ledger.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/20 print:hidden">
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                <span>
                  Showing {((safeCurrentPage - 1) * pageSize) + 1} to {Math.min(safeCurrentPage * pageSize, ledger.length)} of {ledger.length} entries
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="select select-xs select-bordered rounded-lg bg-white dark:bg-restro-gray ml-2"
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
              <div className="join">
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCurrentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  title="First Page"
                >
                  <IconChevronsLeft size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCurrentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <IconChevronLeft size={14} />
                </button>
                <button className="join-item btn btn-xs no-animation font-semibold">
                  Page {safeCurrentPage} of {totalPages}
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <IconChevronRight size={14} />
                </button>
                <button
                  className="join-item btn btn-xs"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  title="Last Page"
                >
                  <IconChevronsRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </ReportExportWrapper>
    </Page>
  );
}
